import { isFulfilled } from "@reduxjs/toolkit";
import React, { useCallback, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router";
import useIsMobile from "../../../hooks/useIsMobile";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { resetCandidateDayCalendarData } from "../../../store/candidate/slice";
import { getCandidateCalendarData } from "../../../store/candidate/thunks";
import { TDayAvailabilityData, TDayData } from "../../../store/candidate/types";
import EditSingleDay from "../../Candidate/Availabilities/EditSingleDay";
import Alert from "../Icons/Alert";
import EditPencil from "../Icons/EditPencil";
import Checkbox from "../Inputs/Checkbox";
import PopupModal from "../Popup";
import CalendarSingDayView from "./CalendarSingDayView";

interface DayCellProps {
  weekName: string;
  dayKey: string;
  dayData: TDayData;
  handleCellChange: (weekKey: string, day: string, checked: boolean) => void;
  onRefresh: () => void;
}

const DayCell: React.FC<DayCellProps> = ({
  weekName,
  dayKey,
  dayData,
  handleCellChange,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const checkboxId = useId();

  const dayAvailability = dayData.availability?.[0] || null;
  const hasAcceptedJobs = dayData?.accepted_count > 0;

  const [showAcceptedJobs, setShowAcceptedJobs] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<TDayData | null>(null);
  const [currentSelectedDay, setCurrentSelectedDay] = useState<string>("");
  const [dayClickTrigger, setDayClickTrigger] = useState<number>(0);

  const calendarDates = useAppSelector(
    (state) => state.candidateJobs.calendarDates
  );
  const maxDistanceAway = useAppSelector(
    (state) => state.candidateJobs.maxDistanceAwayForMyCalendar
  );

  const formatDate = (isoDate: string): React.JSX.Element => {
    const date = new Date(isoDate);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    return (
      <div className="d-flex flex-column flex-lg-row gap-3 align-items-center">
        <div className="fs-16 fw-700 text-black">{day}</div>
        <div className="w-1px"></div>
        <div className="fs-13 fw-500 text-black">{month}</div>
      </div>
    );
  };

  const formatAvailabilities = (arr: TDayAvailabilityData[]): string => {
    if (!arr || arr.length === 0) return "";

    const formatTime = (time?: string | null) => {
      if (!time) return "12:00am"; // Treat null/empty as midnight start

      const [hourStr, minuteStr] = time.split(":");
      const hour = Number(hourStr);
      const minute = Number(minuteStr ?? "0");

      if (isNaN(hour) || isNaN(minute)) return "12:00am";

      let h = hour % 12 || 12;
      let m = minute === 0 ? "" : `:${minute.toString().padStart(2, "0")}`;
      let ampm = hour < 12 ? "am" : "pm";
      return `${h}${m}${ampm}`;
    };

    // If all slots have null start/end (or empty), treat as full day
    const allSlotsEmpty =
      Array.isArray(arr) &&
      arr.every((slot) => !slot.start_time && !slot.end_time);

    if (allSlotsEmpty) {
      return dayAvailability.all_day_available !== 0
        ? "Available 12:00am to 12:00am"
        : "";
    }

    const safeArr = Array.isArray(arr) ? arr : [];
    const ranges = safeArr.map(
      (slot) => `${formatTime(slot.start_time)} to ${formatTime(slot.end_time)}`
    );

    return `Available ${ranges.join(", ")}`;
  };

  const redirectToJobListing = () => {
    navigate(AUTH_ROUTE.JOBS);
  };

  const handleSingleDayClick = async (day: TDayData) => {
    await dispatch(resetCandidateDayCalendarData());
    setCurrentSelectedDay(day?.date);
    setDayClickTrigger((prev) => prev + 1);
  };

  const renderDayContent = (day: TDayData, isMobile: boolean = false) => {
    // Mobile-specific rendering
    if (isMobile) {
      return (
        <div>
          {!hasAcceptedJobs && day.listing_count > 0 && (
            <div className="d-block d-lg-none fs-12 fw-700 text-teal-dark cursor-pointer text-center pt-5px">
              <Alert />
            </div>
          )}
          {day.accepted_count > 0 && (
            <div className="d-block d-lg-none fs-12 fw-700 text-teal-dark cursor-pointer  text-decoration-underline">
              {day.accepted_count} {day.accepted_count > 1 ? "jobs" : "job"}
            </div>
          )}
        </div>
      );
    }

    const renderNotAvailable = () =>
      dayAvailability?.all_day_available === 0 && (
        <div key="not-available" className="fs-13 fw-400 text-grey-medium">
          Not Available
        </div>
      );

    const renderAllDayAvailable = () =>
      dayAvailability?.all_day_available === 1 && (
        <>
          <div
            key="all-day-available"
            className="fs-13 fw-400 text-teal-dark cursor-pointer"
          >
            Available all day{" "}
            <span
              onClick={(e) => {
                e.stopPropagation(); // Prevent parent onClick
                setSelectedDay(day);
              }}
            >
              <EditPencil size={16} color="#757575" />
            </span>
          </div>
        </>
      );

    const renderAcceptedJobDetails = () => {
      if (!hasAcceptedJobs) return null;

      const firstJob = day.accepted_jobs?.[0];
      if (!firstJob) return null;

      return (
        <React.Fragment key="accepted-jobs">
          <div className="fs-12 fw-700 text-black">
            {firstJob.start_time} to {firstJob.end_time}
          </div>
          <div className="fs-13 fw-400 text-black">
            {firstJob?.centre_name}, {firstJob?.centre_address}
          </div>
          {day.accepted_count > 1 && (
            <div className="fs-13 fw-700 text-teal-dark">
              {day?.accepted_count - 1} other job
            </div>
          )}
        </React.Fragment>
      );
    };

    const renderCustomAvailability = () =>
      dayAvailability?.all_day_available !== 1 &&
      day.availability?.length > 0 && (
        <div
          key="custom-availability"
          className="d-flex flex-column gap-3 cursor-pointer text-grey-medium hover-text-black max-w-120px"
        >
          <div className="fs-13 fw-400 text-teal-dark hover-text-black">
            {formatAvailabilities(day.availability)}
          </div>
        </div>
      );

    const renderEditOption = () =>
      (dayAvailability?.all_day_available === 2 ||
        dayAvailability?.all_day_available === 0) && (
        <div
          key="edit"
          className="d-flex gap-2 align-items-center cursor-pointer"
          onClick={(e) => {
            e.stopPropagation(); // Prevent parent onClick
            setSelectedDay(day);
          }}
        >
          <EditPencil size={12} color="#757575" />
          <div className="fs-13 fw-400 text-grey-medium">Edit</div>
        </div>
      );

    const renderJobMatches = () =>
      dayAvailability?.all_day_available !== 0 &&
      !hasAcceptedJobs &&
      day.listing_count > 0 && (
        <div
          key="job-match"
          className="d-flex gap-2 align-items-center cursor-pointer"
          // onClick={redirectToJobListing}
        >
          <Alert />
          <div className="fs-13 fw-400 text-alert hover-text-alert-dark">
            {day.listing_count} job{" "}
            {day.listing_count === 1 ? "match" : "matches"} found
          </div>
        </div>
      );

    const items = [
      renderNotAvailable(),
      renderAllDayAvailable(),
      renderAcceptedJobDetails(),
      renderCustomAvailability(),
      renderEditOption(),
      renderJobMatches(),
    ].filter(Boolean); // remove null/undefined

    return items.length > 0 ? <div>{items}</div> : null;
  };

  const onClickClose = () => {
    setSelectedDay(null);
    setShowAcceptedJobs(false);
    setCurrentSelectedDay("");
    document.body.classList.remove("modal-open");
  };

  const onCLickCloseAcceptedJobs = () => {
    setShowAcceptedJobs(false);
    setCurrentSelectedDay("");
    document.body.classList.remove("modal-open");
    onRefresh && onRefresh();
  };

  const convertToISODate = (dateStr: string): string => {
    const months: { [key: string]: string } = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };
    const [day, mon, year] = dateStr?.split(" ");
    if (!day || !mon || !year || !months[mon]) return "";
    return `${year}-${months[mon]}-${day.padStart(2, "0")}`;
  };

  const loadDayJobs = useCallback(async () => {
    if (!currentSelectedDay) return;
    dispatch(showTransparentPreloaderAction());
    const isoDate = convertToISODate(currentSelectedDay);
    const selectedDayIndex = calendarDates.findIndex(
      (item) => item.date === currentSelectedDay
    );
    const dayData = calendarDates[selectedDayIndex];
    const isDayEmpty =
      dayData?.listing_count === 0 && dayData?.accepted_count === 0;
    const listingType = dayData?.accepted_count > 0 ? "accepted" : "listing";
    const response = await dispatch(
      getCandidateCalendarData({ date: isoDate, type: listingType, distance_away: maxDistanceAway })
    );
    if (isFulfilled(response)) {
      setShowAcceptedJobs(true);
    }
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch, currentSelectedDay, calendarDates, maxDistanceAway]);

  useEffect(() => {
    dayClickTrigger > 0 && loadDayJobs();
    // Only run when currentSelectedDay or calendarDates changes
  }, [loadDayJobs, dayClickTrigger, maxDistanceAway]);

  const getDayCellBgColor = () => {
    if (dayAvailability && dayAvailability.all_day_available === 0) {
      return "bg-off-white border-grey-light";
    } else if (dayData?.accepted_count > 0) {
      return "bg-teal-light border-grey-light";
    } else if (dayAvailability && dayAvailability.all_day_available === 1) {
      return "";
    }
    return "";
  };

  const isPastDate = (dateStr: string): boolean => {
    const currentDate = new Date();
    const parts = dateStr.split(" ");
    const day = parts[0];
    const monthStr = parts[1];
    const yearStr = parts[2]; // Extract year from date string
    
    const month = new Date(`${monthStr} 1`).getMonth(); // e.g., 'Jun' to 5
    const year = yearStr ? parseInt(yearStr) : currentDate.getFullYear();
    const date = new Date(year, month, parseInt(day));

    return date < new Date(currentDate.toDateString()); // Only date comparison
  };

  return (
    <>
      {selectedDay && (
        <PopupModal
          visible={selectedDay !== null}
          showCloseBtn={true}
          onClickClose={onClickClose}
        >
          <EditSingleDay
            dayData={selectedDay}
            dayKey={dayKey}
            onRefresh={onRefresh}
            onClickClose={onClickClose}
            setCurrentSelectedDay={setCurrentSelectedDay}
          />
        </PopupModal>
      )}

      {currentSelectedDay && (
        <PopupModal
          visible={showAcceptedJobs}
          showCloseBtn={true}
          onClickClose={onCLickCloseAcceptedJobs}
          popUpClass="mobile-model"
        >
          <CalendarSingDayView
            formatAvailabilities={formatAvailabilities}
            weekName={weekName}
            handleCellChange={handleCellChange}
            singleDayData={dayData}
            currentSelectedDay={currentSelectedDay}
            setCurrentSelectedDay={setCurrentSelectedDay}
            dayKey={dayKey}
            onRefresh={onRefresh}
            onClickClose={onClickClose}
            setShowAcceptedJobs={setShowAcceptedJobs}
            loadDayJobs={loadDayJobs}
            selectedDay={selectedDay}
          />
        </PopupModal>
      )}
      <td
        className={`align-top text-start ${getDayCellBgColor()} ${
          !isPastDate(dayData.date) ? "" : "disable-div"
        }`}
      >
        <div
          className={`d-flex flex-column gap-5 min-h-83px cursor-pointer text-break ${
            isPastDate(dayData.date) ? "opacity-50" : ""
          }`}
          onClick={() => handleSingleDayClick(dayData)}
        >
          <div className="d-flex flex-column flex-lg-row gap-3 align-items-center justify-content-between">
            {formatDate(dayData.date)}
            <div
              className="theme-check"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
              className="theme-check-input"
              id={checkboxId}
              checked={dayData.checked}
              onChange={(checked: boolean) => {
                handleCellChange(weekName, dayKey, checked);
              }}
              label={""}
              themeCheck={true}
              labelBold={true}
              disabled={hasAcceptedJobs || isPastDate(dayData.date)}
              labelClass="pl-24px checked-teal checked-grey-light"
              />
            </div>
          </div>
          <div className="d-none d-lg-block">{renderDayContent(dayData)}</div>          
        </div>
        <div className="d-block d-lg-none">
            {renderDayContent(dayData, true)}
        </div>
      </td>
    </>
  );
};

export default DayCell;
